import {
  SVButton,
  SVForm,
  SVPasswordInput,
  SVTextInput,
} from '@/components/core';
import { FORM_FIELDS } from '@/constants/formFields';
import { MESSAGES } from '@/constants/messages';
import { useInitiateAuth } from '@/hooks/requests/useAuth';
import { useGetUser } from '@/hooks/requests/useUser';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import showToast from '@/utils/toast';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

type ProfileSettings2FAFieldValues = {
  password: string;
  code: string;
};

type Update2FAModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  afterSuccessCallback: (password: string) => void;
};

export default function Update2FAModal({
  open,
  setOpen,
  afterSuccessCallback,
}: Update2FAModalProps) {
  const { isStaff } = useAuthUser();
  const { user } = useGetUser();
  const { authUser, setAuthUser } = useAuthUser();
  const [isOnPasswordStep, setIsOnPasswordStep] = useState(true);
  const {
    initateAuth,
    authChallengeValidation,
    isLoadingInitateAuth,
    isLoadingAuthChallengeValidation,
  } = useInitiateAuth();

  const formMethods = useForm<ProfileSettings2FAFieldValues>({
    defaultValues: {
      password: '',
      code: '',
    },
  });

  function closeModalSuccessfully() {
    const password = formMethods.getValues('password');
    setOpen(false);
    afterSuccessCallback(password);
  }

  function handlePasswordError(isServerError?: boolean) {
    showToast({
      type: 'error',
      data: {
        content: isServerError
          ? MESSAGES.genericError
          : 'Your password is incorrect',
      },
    });
  }

  async function handlePasswordStep(data: ProfileSettings2FAFieldValues) {
    try {
      const response = await initateAuth(
        {
          username: user?.email.toLowerCase() || '',
          password: data.password,
          userGroup: isStaff ? 'staff' : 'teammate',
        },
        {
          onError: () => handlePasswordError(true),
        },
      );

      if (!response.success) {
        handlePasswordError();
        return;
      }

      setAuthUser(response);

      if (response.success && response.idToken) {
        closeModalSuccessfully();
      } else {
        setIsOnPasswordStep(false);
      }
    } catch {
      handlePasswordError();
    }
  }

  function handleOtpError(isServerError?: boolean) {
    showToast({
      type: 'error',
      data: {
        content: isServerError ? MESSAGES.genericError : 'Invalid OTP code',
      },
    });
  }

  async function handleOtpStep(data: ProfileSettings2FAFieldValues) {
    try {
      const response = await authChallengeValidation(
        {
          username: user?.email.toLowerCase() || '',
          sms_mfa_code: data.code,
          session: authUser?.session || '',
        },
        {
          onError: () => handleOtpError(true),
        },
      );

      if (!response.success) {
        handleOtpError();
        return;
      }

      if (response.success && response.idToken) {
        setAuthUser(response);
        closeModalSuccessfully();
      }
    } catch {
      handleOtpError();
    }
  }

  function handleSubmit(data: ProfileSettings2FAFieldValues) {
    if (isOnPasswordStep) {
      handlePasswordStep(data);
    } else {
      handleOtpStep(data);
    }
  }

  return (
    <Dialog fullWidth open={open} onClose={() => setOpen(false)} maxWidth="md">
      <SVForm<ProfileSettings2FAFieldValues>
        methods={formMethods}
        onSubmit={handleSubmit}>
        <DialogTitle variant="h4">
          {isOnPasswordStep
            ? 'Confirm Your Password'
            : 'Enter Your One Time Password'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {isOnPasswordStep ? (
              <>
                <Typography variant="body1" component="p" pb={2}>
                  Please enter your current password to confirm your identity.
                </Typography>
                <SVPasswordInput
                  name="password"
                  bypassValidation
                  {...FORM_FIELDS.PASSWORD}
                />
              </>
            ) : (
              <SVTextInput
                name="code"
                label="Code"
                htmlInputProps={{ maxLength: 6 }}
                rules={{ required: true }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {!isOnPasswordStep && (
            <SVButton
              isLoading={isLoadingInitateAuth}
              variant="outlined"
              onClick={() => {
                handlePasswordStep(formMethods.getValues());
              }}>
              Resend Code
            </SVButton>
          )}
          <SVButton
            isLoading={isLoadingInitateAuth || isLoadingAuthChallengeValidation}
            variant="primary"
            onClick={formMethods.handleSubmit(handleSubmit)}
            type="submit">
            Submit
          </SVButton>
        </DialogActions>
      </SVForm>
    </Dialog>
  );
}
